import dayjs, { Dayjs } from 'dayjs'

import { DEFAULT_LOCALE } from '@/libs/config/client'
import { combineString, isEmpty } from '@/utils/string'
import { TFnType } from '@tolgee/react'

export const isSameDate = (
  d1: string | Date | Dayjs,
  d2?: string | Date | Dayjs,
): boolean => dayjs(d1).isSame(dayjs(d2), 'day')

export const isToday = (day: string | Date): boolean => isSameDate(day)

export const isTomorrow = (day: string | Date) =>
  isSameDate(dayjs(new Date()).add(1, 'day'), day)

const toJSLocale = (locale: string) => {
  // WORKAROUND: JS follow a different language code standard.
  if (locale === 'bm_MY') return 'ms-MY'
  return locale.replace('_', '-')
}

export const formatDate = ({
  t,
  date,
  locale,
  options = {
    month: 'short',
    day: 'numeric',
  },
}: {
  t: (x: string) => string
  date: string | Date
  locale: string
  options: Record<string, string | undefined>
}): string => {
  // Only support today / tomorrow
  let day = null
  if (isToday(date)) {
    day = t('Today')
  } else if (isTomorrow(date)) {
    day = t('Tomorrow')
  }

  try {
    const formattedLocale = toJSLocale(locale || DEFAULT_LOCALE)
    return day && options.weekday
      ? `${day}, ${new Date(date).toLocaleDateString(formattedLocale, {
          ...options,
          weekday: undefined,
        })}`
      : new Date(date).toLocaleDateString(formattedLocale, options)
  } catch (e) {
    return day && options.weekday
      ? `${day}, ${new Date(date).toLocaleDateString(DEFAULT_LOCALE, {
          ...options,
          weekday: undefined,
        })}`
      : new Date(date).toLocaleDateString(DEFAULT_LOCALE, options)
  }
}

export const formatFullDate = (params: {
  t: (x: string) => string
  date: string | Date
  locale: string
}) =>
  formatDate({
    ...params,
    options: {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
  })

const groupConsecutiveDays = (days: number[]): number[][] =>
  days
    // https://stackoverflow.com/questions/47906850/javascript-group-the-numbers-from-an-array-with-series-of-consecutive-numbers
    .reduce((group: number[][], day: number) => {
      const lastSubArray = group[group.length - 1]
      if (!lastSubArray || lastSubArray[lastSubArray.length - 1] !== day - 1) {
        group.push([])
      }
      const currentGroup = group[group.length - 1]
      if (currentGroup) {
        currentGroup.push(day)
      }
      return group
    }, [])

const dayStringToArrayOfDays = (daysString = '') =>
  daysString
    .split(',')
    .map((d) => getTranslatedOMSWeekDays().indexOf(d.trim()))
    .filter((d) => d > -1)
    .sort()

export const getTranslatedWeekDays = (t: TFnType, weekDay = 'short') =>
  weekDay === 'short'
    ? [t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat')]
    : [
        t('Sunday'),
        t('Monday'),
        t('Tuesday'),
        t('Wednesday'),
        t('Thursday'),
        t('Friday'),
        t('Saturday'),
      ]

export const getTranslatedOMSWeekDays = (
  t = (x: string) => x,
  weekDay = 'short',
) => {
  return weekDay === 'short'
    ? [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')]
    : [
        t('Monday'),
        t('Tuesday'),
        t('Wednesday'),
        t('Thursday'),
        t('Friday'),
        t('Saturday'),
        t('Sunday'),
      ]
}

export const formatDayRange = (
  daysString: string,
  weekDay: string,
  t: TFnType,
) => {
  const longDaysOfWeek = getTranslatedOMSWeekDays(t, weekDay)
  return groupConsecutiveDays(dayStringToArrayOfDays(daysString))
    .reduce((flatten: string[], group: number[]) => {
      if (group.length === 1) {
        const index = group[0]
        if (
          index !== undefined &&
          index >= 0 &&
          index < longDaysOfWeek.length
        ) {
          const day = longDaysOfWeek[index]
          if (day) {
            return flatten.concat(day)
          }
        }
        return flatten
      } else if (group.length === 2) {
        const days = group
          .filter((index) => index >= 0 && index < longDaysOfWeek.length)
          .map((index) => longDaysOfWeek[index])
          .filter((day): day is string => day !== undefined)
        return flatten.concat(days)
      } else {
        const firstIndex = group[0]
        const lastIndex = group[group.length - 1]
        if (
          firstIndex !== undefined &&
          firstIndex >= 0 &&
          firstIndex < longDaysOfWeek.length &&
          lastIndex !== undefined &&
          lastIndex >= 0 &&
          lastIndex < longDaysOfWeek.length
        ) {
          const firstDay = longDaysOfWeek[firstIndex]
          const lastDay = longDaysOfWeek[lastIndex]
          if (firstDay && lastDay) {
            return flatten.concat(`${firstDay} - ${lastDay}`)
          }
        }
        return flatten
      }
    }, [])
    .join(', ')
}

export const parseAvailableTime = (availableTime: string, splitBy: string) => {
  const fromDate = new Date()
  const toDate = new Date()

  if (isEmpty(availableTime)) {
    return { fromDate, toDate }
  }

  const [from, to] = (availableTime || '').split(splitBy)
  const fromParts = from?.match(/(\d+):(\d+) (\w+)/)
  let fromHours = 0
  let fromMinutes = 0
  if (fromParts && fromParts.length === 4) {
    const [, hoursStr, minutesStr, ampmStr] = fromParts
    if (
      hoursStr !== undefined &&
      minutesStr !== undefined &&
      ampmStr !== undefined
    ) {
      fromHours = (+hoursStr % 12) + (/pm/i.test(ampmStr) ? 12 : 0)
      fromMinutes = +minutesStr
    }
  }

  const toParts = to?.match(/(\d+):(\d+) (\w+)/)
  let toHours = 0
  let toMinutes = 0
  if (toParts && toParts.length === 4) {
    const [, hoursStr, minutesStr, ampmStr] = toParts
    if (
      hoursStr !== undefined &&
      minutesStr !== undefined &&
      ampmStr !== undefined
    ) {
      toHours = (+hoursStr % 12) + (/pm/i.test(ampmStr) ? 12 : 0)
      toMinutes = +minutesStr
    }
  }

  fromDate.setHours(fromHours, fromMinutes, 0, 0)
  toDate.setHours(toHours, toMinutes, 0, 0)

  return { fromDate, toDate }
}

/**
 * This function will replace 12:00 AM into 00:00 AM
 * and put the word ("midnight") after the time if it is within 12:00 AM to 01:00 AM hours
 * The product requirement can be found in the following ticket
 * https://oddle-pm.atlassian.net/browse/FESC-1444
 */
export const formatLabelTimeSlot = (t: TFnType, label: string) => {
  if (label === t('Please select a timeslot')) {
    return label
  }

  const { fromDate, toDate } = parseAvailableTime(label, ' to ')

  // Late night is from 00:00 AM to 05:59 Am
  const isLateNight = fromDate.getHours() >= 0 && fromDate.getHours() <= 5

  // Only modify time if it is within 12:00 AM to 06:00 AM hours
  if (!isLateNight) {
    return label
  }

  // Format the date into HH:MM aa format
  // en-GB locale is used since it will display 12:00 am as 00:00 am in Google Chrome Version 80.0
  const fromDateString = fromDate.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  const toDateString = toDate.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  // Combine fromDate and toDate into "HH:MM AA to HH:MM AA (late night)" format
  return (
    combineString(
      fromDateString,
      'to',
      toDateString,
      isLateNight ? t('(late night)') : undefined,
    )
      .replace(/am|pm/g, (match) => match.toUpperCase())
      // Make sure 00:00 AM will be displayed across all browsers
      .replace(/12:/g, (match) => (isLateNight ? '00:' : match))
  )
}

export const parseDateString = (string: string, format?: string): Date =>
  dayjs(string, format).toDate()

export const dayMatchesModifier = (
  day: Date,
  disabledDays: {
    before: Date
    after: Date
  },
): boolean => {
  const { before, after } = disabledDays
  const beforeMatch = before ? dayjs(day).isBefore(before, 'day') : false
  const afterMatch = after ? dayjs(day).isAfter(after, 'day') : false
  return beforeMatch || afterMatch
}

export const getTranslatedMonths = (t: TFnType, month = 'long'): string[] =>
  month === 'short'
    ? [
        t('Jan'),
        t('Feb'),
        t('Mar'),
        t('Apr'),
        t('May'),
        t('Jun'),
        t('Jul'),
        t('Aug'),
        t('Sep'),
        t('Oct'),
        t('Nov'),
        t('Dec'),
      ]
    : [
        t('January'),
        t('February'),
        t('March'),
        t('April'),
        t('May'),
        t('June'),
        t('July'),
        t('August'),
        t('September'),
        t('October'),
        t('November'),
        t('December'),
      ]

export const formatShortDate = (params: {
  t: TFnType
  date: string | Date
  locale: string
}) =>
  formatDate({
    ...params,
    options: {
      weekday: 'short',
      year: undefined,
      month: 'short',
      day: 'numeric',
    },
  })

export function getTimestampOfMidnightForCountry(gmtOffset = 0) {
  const now = new Date()

  // Get tomorrow's date
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)

  // Get midnight UTC for tomorrow
  const year = tomorrow.getUTCFullYear()
  const month = String(tomorrow.getUTCMonth() + 1).padStart(2, '0')
  const day = String(tomorrow.getUTCDate()).padStart(2, '0')

  const midnightUTC = new Date(
    `${year}-${month}-${day}T00:00:00.000Z`,
  ).getTime()

  // Subtract the GMT offset (in hours) to get midnight in the target timezone
  return midnightUTC - gmtOffset * 60 * 60 * 1000
}

export const getDay = (date: string): string => {
  const getDay = new Date(date).getDay()
  const days = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
  } as Record<number, string>

  return days[getDay] as string
}

export const dayOfPickupOrDelivery = (date: string, t: TFnType) => {
  const day = getDay(date)
  if (isToday(date)) return t('Today')
  if (isTomorrow(date)) return t('Tomorrow')

  return t('{day}', {
    day,
  })
}

export const getMonthFromShortNameOfMonthToNumber = (month: string) => {
  const months = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  } as Record<string, number>

  return months[month] as number
}

export const convertTo24Hour = (time12h?: string) => {
  if (!time12h) return ''
  const [time, modifier] = time12h.split(' ')
  if (!time) return ''
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(':')
  if (!hours) return ''

  if (hours === '12') {
    hours = '00' // Midnight (12 AM) becomes 00, Noon (12 PM) remains 12
  }

  if (modifier === 'PM') {
    hours = (parseInt(hours, 10) + 12).toString()
  }

  return `${hours}:${minutes || '00'}`
}

export const parseCollectionTimeSlot = (
  encodedTimeSlot: string,
): string | null => {
  // URL decode: "08%3A00+AM+to+08%3A30+AM" → "08:00 AM to 08:30 AM"
  const decoded = decodeURIComponent(encodedTimeSlot.replace(/\+/g, ' '))

  // Split by " to "
  const [startTime, endTime] = decoded.split(' to ')

  // Convert 12h to 24h format
  const to24Hour = (time12h: string): string => {
    const [time, period] = time12h.trim().split(' ')

    if (time) {
      // eslint-disable-next-line prefer-const
      let [hours, minutes] = time.split(':').map(Number)

      if (period === 'PM' && hours !== 12) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        hours += 12
      } else if (period === 'AM' && hours === 12) {
        hours = 0
      }

      return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}`
    }
    return `${time}:${period}`
  }

  if (startTime && endTime) {
    return `${to24Hour(startTime)}-${to24Hour(endTime)}`
  }

  return null
}
